// Copyright (c) Docugami, Inc. All rights reserved.

import { ColorPicker, getColorFromString, IColor, IContextualMenuItem } from "@fluentui/react";
import { appContentColor, primaryTheme, themePink } from "components/shell/presentation/AppStyles";
import { dgIconNames, useDgIcons } from "hooks/dgIcons";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import { ReportEntry } from "networking/reportsApi";
import { useEffect, useMemo } from "react";
import { selectReportEntryBindingsForCurrentDocument } from "store/reports/selectors";
import { removePreviewChunkSet, setPreviewChunks, setPreviewChunkSetColor } from "store/sdvSlice";
import { getReportEntryPreviewColor, setReportEntryPreviewColor } from "utils/localStorageHelpers";

interface PreviewButtonProps {
    entry: ReportEntry;
}

export function usePreviewChunksCommandProps(props: PreviewButtonProps): IContextualMenuItem {
    const { entry } = props;

    const previewData = useAppSelector(state => state.sdv.document?.previewChunkSets[entry.id]);
    const bindings = useAppSelector(state => selectReportEntryBindingsForCurrentDocument(state)?.entities[entry.id]?.bindings);
    const isPreviewOn = !!previewData;
    const spanIds = useMemo(() => bindings?.map(x => x.spanId)?.filter(x => !!x) as string[] | undefined, [bindings]);
    const color = useMemo(() => getColorFromString(previewData?.color ?? getReportEntryPreviewColor(entry.reportId, entry.id) ?? themePink)!, [previewData, entry]);

    const isEnabled = !!spanIds?.length;

    const dispatch = useAppDispatch();
    const { icon } = useDgIcons();

    useEffect(() => {
        // Handle bindings change for the entry
        if (!isPreviewOn) {
            return;
        }

        if (!!spanIds?.length) {
            dispatch(setPreviewChunks({ id: entry.id, chunks: { spanIds: spanIds, color: `#${color.hex}` } }))
        }
        else {
            dispatch(removePreviewChunkSet(entry.id));
        }
    },  // eslint-disable-next-line react-hooks/exhaustive-deps
        [spanIds])

    function togglePreview(color: string) {
        if (isPreviewOn) {
            dispatch(removePreviewChunkSet(entry.id));
        }
        else if (!!spanIds?.length) {
            dispatch(setPreviewChunks({ id: entry.id, chunks: { spanIds: spanIds, color: color } }));
        }
    }

    function onColorChange(ev: any, colorObj: IColor) {
        dispatch(setPreviewChunkSetColor({ id: entry.id, color: `#${colorObj.hex}` }));
        if (!isPreviewOn) {
            togglePreview(`#${color.hex}`);
        }
    }

    function renderIcon() {
        return icon(
            dgIconNames.DgEyeFill,
            'Show all chunks',
            30,
            15,
            isPreviewOn ? `#${color.hex}` : primaryTheme.palette.white,
            '',
            '',
            appContentColor
        )
    }

    return {
        key: 'preview_button',
        name: 'Preview',
        disabled: !isEnabled,
        onMouseDown: !isEnabled ? undefined : () => togglePreview(`#${color.hex}`),
        iconProps: { iconName: '' },
        onRenderIcon: renderIcon,
        subMenuProps: {
            isSubMenu: true,
            items: [{ key: 'color_picker' }],
            onMenuDismissed: () => setReportEntryPreviewColor(entry.reportId, entry.id, previewData?.color),
            onRenderContextualMenuItem: () => (
                <ColorPicker
                    color={color}
                    alphaType="none"
                    onChange={onColorChange} />
            )
        }
    }
}
